import React, { useState, useEffect } from 'react'
import { loginUser, setAuthFromStorage } from '../globals.js'
import ActivateScreen from './ActivateScreen.jsx'

function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Handle Google OAuth return: /?google_auth=... or /?google_error=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const googleAuth = params.get('google_auth')
    const googleError = params.get('google_error')

    if (googleAuth) {
      try {
        const data = JSON.parse(decodeURIComponent(googleAuth))
        localStorage.setItem('oike_token', data.token)
        localStorage.setItem('oike_user', JSON.stringify(data.user))
        localStorage.setItem('oike_base_id', data.baseId || '')
        if (data.config) localStorage.setItem('oike_config', JSON.stringify(data.config))
        setAuthFromStorage()
        window.history.replaceState({}, '', '/')
        onLogin()
      } catch (e) {
        setError('Google sign-in failed. Try again.')
        window.history.replaceState({}, '', '/')
      }
    }

    if (googleError) {
      setError(decodeURIComponent(googleError).replace(/_/g, ' '))
      window.history.replaceState({}, '', '/')
    }
  }, [])

  if (mode === 'activate') {
    return <ActivateScreen onBack={() => setMode('login')} />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email || !password) { setError('Enter your email and password'); return }
    setError('')
    setLoading(true)
    try {
      await loginUser(email.trim(), password)
      onLogin()
    } catch (err) {
      setError(err.message || 'Invalid credentials')
    }
    setLoading(false)
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--globant-darker)' }}>
      <div style={{ width: 380, background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 16, padding: 40, textAlign: 'center' }}>
        <div style={{ width: 56, height: 56, background: '#1F7A70', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 24, color: 'var(--globant-dark)', margin: '0 auto 20px' }}>O</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Welcome to Oike</h2>
        <p style={{ fontSize: 13, color: 'var(--globant-muted)', marginBottom: 28 }}>Sales Intelligence Platform</p>

        {/* Google Sign-In — server-side redirect flow, no JS origin issues */}
        <a
          href="/api/auth/google/start"
          style={{ width: '100%', padding: '12px 16px', background: '#fff', border: '1px solid #dadce0', borderRadius: 8, color: '#3c4043', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 20, textDecoration: 'none', boxSizing: 'border-box' }}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)' }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google
        </a>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: 'var(--globant-border)' }} />
          <span style={{ fontSize: 11, color: 'var(--globant-muted)' }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'var(--globant-border)' }} />
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14, textAlign: 'left' }}>
            <label style={{ fontSize: 11, color: 'var(--globant-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' }}>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              style={{ width: '100%', padding: '12px 14px', background: 'var(--globant-darker)', border: '1px solid var(--globant-border)', borderRadius: 8, color: 'var(--globant-text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              autoFocus
            />
          </div>
          <div style={{ marginBottom: 20, textAlign: 'left' }}>
            <label style={{ fontSize: 11, color: 'var(--globant-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter your password"
              style={{ width: '100%', padding: '12px 14px', background: 'var(--globant-darker)', border: '1px solid var(--globant-border)', borderRadius: 8, color: 'var(--globant-text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>

          {error && <div style={{ color: 'var(--globant-danger)', fontSize: 12, marginBottom: 14, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}

          <button
            type="submit"
            disabled={loading}
            style={{ width: '100%', padding: '13px', background: loading ? 'var(--globant-border)' : 'var(--globant-green)', border: 'none', borderRadius: 8, color: 'var(--globant-dark)', fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={{ fontSize: 12, color: 'var(--globant-muted)', marginTop: 20, cursor: 'pointer' }} onClick={() => setMode('activate')}>
          Have an invite code? <strong style={{ color: 'var(--globant-green)' }}>Activate account</strong>
        </p>

        <p style={{ fontSize: 11, color: 'var(--globant-muted)', marginTop: 16 }}>Powered by <strong style={{ color: 'var(--globant-green)' }}>Oike</strong></p>
      </div>
    </div>
  )
}

export default LoginScreen
